import { useEffect, useState } from 'react';
import { hubAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { MessageSquare, Lightbulb, ThumbsUp, Send, Trash2, Sparkles, Rocket, CheckCircle2, Reply } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Hint, InfoHint } from '@/components/common/Hint';
import { Linkify } from '@/lib/linkify';
import { MentionTextInput, extractMentionIds, mentionIdsToNames } from './MentionTextInput';
import { QuickReactionBar } from './QuickReactionBar';

const timeAgo = (iso) => {
    if (!iso) return '';
    const diffMin = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (diffMin < 1) return "à l'instant";
    if (diffMin < 60) return `il y a ${diffMin} min`;
    const h = Math.floor(diffMin / 60);
    if (h < 24) return `il y a ${h} h`;
    const d = Math.floor(h / 24);
    if (d < 30) return `il y a ${d} j`;
    return new Date(iso).toLocaleDateString('fr-FR');
};

export function GeneralWall({ spaces, themes, members, canCreateProjects, onProjectCreated }) {
    const { user } = useAuth();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [newPost, setNewPost] = useState({ content: '', post_type: 'discussion' });
    const [converting, setConverting] = useState(null); // post to convert

    const load = async () => {
        setLoading(true);
        try {
            const r = await hubAPI.listWall();
            setPosts(r.data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const submitPost = async () => {
        if (!newPost.content.trim()) return;
        try {
            const mentions = extractMentionIds(newPost.content.trim(), members || []);
            const r = await hubAPI.createPost(newPost.content, newPost.post_type, mentions);
            setPosts((prev) => [r.data, ...prev]);
            setCreating(false);
            setNewPost({ content: '', post_type: 'discussion' });
            toast.success(newPost.post_type === 'proposition' ? 'Proposition publiée 💡' : 'Message publié');
            if (mentions.length > 0) {
                toast.success(`${mentions.length} personne${mentions.length > 1 ? 's' : ''} mentionnée${mentions.length > 1 ? 's' : ''}`);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        }
    };

    const toggleVote = async (post) => {
        try {
            const r = await hubAPI.votePost(post.id);
            setPosts((prev) => prev.map((p) => (p.id === post.id ? {
                ...p,
                votes_count: r.data.votes_count,
                voted_by_me: r.data.voted_by_me,
                votes_up: r.data.voted_by_me ? [...(p.votes_up || []), user.id] : (p.votes_up || []).filter((x) => x !== user.id),
            } : p)));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        }
    };

    const addComment = async (post, content, mentions = []) => {
        try {
            const r = await hubAPI.commentPost(post.id, content, mentions);
            setPosts((prev) => prev.map((p) => (p.id === post.id ? r.data : p)));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        }
    };

    const deleteComment = async (post, commentId) => {
        try {
            const r = await hubAPI.deleteComment(post.id, commentId);
            setPosts((prev) => prev.map((p) => (p.id === post.id ? r.data : p)));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        }
    };

    const deletePost = async (post) => {
        if (!window.confirm('Supprimer ce message ?')) return;
        try {
            await hubAPI.deletePost(post.id);
            setPosts((prev) => prev.filter((p) => p.id !== post.id));
            toast.success('Supprimé');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        }
    };

    const togglePostReaction = async (post, emoji) => {
        try {
            const r = await hubAPI.toggleWallPostReaction(post.id, emoji);
            setPosts((prev) => prev.map((p) => (p.id === post.id ? r.data : p)));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        }
    };

    const toggleCommentReaction = async (post, commentId, emoji) => {
        try {
            const r = await hubAPI.toggleWallCommentReaction(post.id, commentId, emoji);
            setPosts((prev) => prev.map((p) => (p.id === post.id ? r.data : p)));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        }
    };

    // Sort: propositions ouvertes triées par votes desc, puis discussions
    const sortedPosts = [...posts].sort((a, b) => {
        const aIsProp = a.post_type === 'proposition' && a.status !== 'converted';
        const bIsProp = b.post_type === 'proposition' && b.status !== 'converted';
        if (aIsProp && !bIsProp) return -1;
        if (!aIsProp && bIsProp) return 1;
        if (aIsProp && bIsProp) return (b.votes_count || 0) - (a.votes_count || 0);
        return new Date(b.created_at) - new Date(a.created_at);
    });

    return (
        <Card data-testid="general-wall">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className="text-base text-slate-800 flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-indigo-500" /> Mur d&apos;idées & propositions
                        <InfoHint text="Postez ici toute idée, veille, opportunité repérée. En cliquant sur « Proposer un projet », votre message peut être voté par l'équipe puis converti en vrai projet en un clic." />
                    </CardTitle>
                    <div className="flex gap-2">
                        <Hint text="Publier un simple message ou une question à l'équipe (pas destinée à devenir un projet).">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => { setNewPost({ content: '', post_type: 'discussion' }); setCreating(true); }}
                                data-testid="btn-new-discussion"
                                className="gap-1"
                            >
                                <MessageSquare className="w-4 h-4" /> Discuter
                            </Button>
                        </Hint>
                        <Hint text="Proposer une opportunité repérée pour la transformer en projet. Les autres peuvent voter — si l'idée plait, un admin la convertit en projet officiel.">
                            <Button
                                size="sm"
                                onClick={() => { setNewPost({ content: '', post_type: 'proposition' }); setCreating(true); }}
                                data-testid="btn-new-proposition"
                                className="gap-1 bg-amber-500 hover:bg-amber-600"
                            >
                                <Lightbulb className="w-4 h-4" /> Proposer un projet
                            </Button>
                        </Hint>
                    </div>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                    Repéré un truc intéressant ? Une opportunité ? Postez ici, discutez avec l&apos;équipe, votez — et transformez la meilleure idée en vrai projet.
                </p>
            </CardHeader>
            <CardContent>
                {loading ? (
                    <div className="text-sm text-slate-400 py-4">Chargement…</div>
                ) : sortedPosts.length === 0 ? (
                    <div className="text-center py-8 text-slate-400 text-sm">
                        <Sparkles className="w-10 h-10 mx-auto mb-2 opacity-30" />
                        Aucun message pour l&apos;instant.
                        <div className="text-[11px] mt-1">Publiez la première idée !</div>
                    </div>
                ) : (
                    <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1">
                        {sortedPosts.map((p) => (
                            <PostItem
                                key={p.id}
                                post={p}
                                currentUser={user}
                                members={members}
                                onVote={() => toggleVote(p)}
                                onComment={(c, mentions) => addComment(p, c, mentions)}
                                onDeleteComment={(cid) => deleteComment(p, cid)}
                                onDelete={() => deletePost(p)}
                                onReactPost={(emoji) => togglePostReaction(p, emoji)}
                                onReactComment={(cid, emoji) => toggleCommentReaction(p, cid, emoji)}
                                onConvert={canCreateProjects ? () => setConverting(p) : null}
                            />
                        ))}
                    </div>
                )}
            </CardContent>

            {/* Dialog nouveau post */}
            <Dialog open={creating} onOpenChange={setCreating}>
                <DialogContent data-testid="new-post-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {newPost.post_type === 'proposition' ? <><Lightbulb className="w-4 h-4 text-amber-500" /> Proposer un projet</> : <><MessageSquare className="w-4 h-4" /> Nouveau message</>}
                        </DialogTitle>
                        <DialogDescription>
                            {newPost.post_type === 'proposition'
                                ? 'Décrivez l\'opportunité repérée. L\'équipe pourra voter, commenter, et un admin pourra la convertir en vrai projet.'
                                : 'Discutez librement avec l\'équipe sur le module Projets.'}
                        </DialogDescription>
                    </DialogHeader>
                    <MentionTextInput
                        multiline
                        rows={5}
                        value={newPost.content}
                        onChange={(v) => setNewPost({ ...newPost, content: v })}
                        onSubmit={submitPost}
                        members={members || []}
                        placeholder={newPost.post_type === 'proposition' ? "Ex : Les gars regardez ce que j'ai vu sur les réseaux, des mecs vendent X. Il doit y avoir une fiche CEE là-dessus, on devrait creuser…  ·  Tapez @ pour mentionner un membre  ·  Ctrl+Entrée pour publier" : "Un message pour l'équipe…  ·  Tapez @ pour mentionner un membre  ·  Ctrl+Entrée pour publier"}
                        testId="new-post-content"
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCreating(false)}>Annuler</Button>
                        <Button onClick={submitPost} data-testid="new-post-submit" className={newPost.post_type === 'proposition' ? 'bg-amber-500 hover:bg-amber-600' : ''}>
                            Publier
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {converting && (
                <ConvertToProjectDialog
                    post={converting}
                    open={!!converting}
                    onOpenChange={(o) => { if (!o) setConverting(null); }}
                    spaces={spaces}
                    themes={themes}
                    members={members}
                    onDone={(p) => {
                        toast.success('Projet créé depuis la proposition 🚀');
                        setPosts((prev) => prev.map((x) => x.id === converting.id ? { ...x, status: 'converted', converted_to_project_id: p.id } : x));
                        setConverting(null);
                        onProjectCreated?.();
                    }}
                />
            )}
        </Card>
    );
}

function PostItem({ post, currentUser, members, onVote, onComment, onDeleteComment, onDelete, onConvert, onReactPost, onReactComment }) {
    const [showComments, setShowComments] = useState((post.comments || []).length > 0);
    const [commentInput, setCommentInput] = useState('');
    const isAuthor = post.author_id === currentUser?.id;
    const isProposition = post.post_type === 'proposition';
    const isConverted = post.status === 'converted';
    const mentionNames = mentionIdsToNames(post.mentions, members);

    const submitComment = () => {
        if (!commentInput.trim()) return;
        const mentions = extractMentionIds(commentInput.trim(), members || []);
        onComment(commentInput.trim(), mentions);
        setCommentInput('');
        if (mentions.length > 0) {
            toast.success(`${mentions.length} personne${mentions.length > 1 ? 's' : ''} mentionnée${mentions.length > 1 ? 's' : ''}`);
        }
    };

    return (
        <div
            className={cn(
                "rounded-lg border p-3 space-y-2 relative transition-all",
                post.unread && "ring-2 ring-red-300/60 shadow-md",
                isConverted ? "border-emerald-200 bg-emerald-50/30" :
                    post.unread ? "border-red-200 bg-red-50/20" :
                    isProposition ? "border-amber-200 bg-amber-50/30" : "border-slate-200 bg-white"
            )}
            data-testid={`post-${post.id}`}
        >
            {post.unread && (
                <span
                    className="absolute -top-2 -right-2 bg-red-500 text-white text-[9px] px-2 py-0.5 rounded-full font-bold shadow ring-2 ring-white"
                    data-testid={`post-new-tag-${post.id}`}
                >
                    NOUVEAU
                </span>
            )}
            {/* Header : auteur + badges */}
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 text-[11px] flex items-center justify-center font-bold">
                        {(post.author?.first_name?.[0] || '?')}{(post.author?.last_name?.[0] || '')}
                    </span>
                    <div>
                        <div className="text-xs font-semibold text-slate-800">
                            {post.author?.first_name} {post.author?.last_name}
                        </div>
                        <div className="text-[10px] text-slate-400">{timeAgo(post.created_at)}</div>
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    {isProposition && !isConverted && (
                        <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 gap-1 text-[10px]">
                            <Lightbulb className="w-3 h-3" /> Proposition
                        </Badge>
                    )}
                    {isConverted && (
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 gap-1 text-[10px]">
                            <CheckCircle2 className="w-3 h-3" /> Convertie en projet
                        </Badge>
                    )}
                    {isAuthor && (
                        <Hint text="Supprimer votre message">
                            <button onClick={onDelete} className="text-slate-300 hover:text-red-500" data-testid={`post-delete-${post.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        </Hint>
                    )}
                </div>
            </div>

            {/* Contenu — mentions surlignées */}
            <div className="text-sm text-slate-700 whitespace-pre-wrap break-words" data-testid={`post-content-${post.id}`}>
                <Linkify text={post.content} mentionNames={mentionNames} />
            </div>

            {/* Réactions rapides (1-tap) */}
            {onReactPost && (
                <QuickReactionBar
                    reactions={post.reactions}
                    currentUserId={currentUser?.id}
                    onToggle={onReactPost}
                    testIdPrefix={`post-${post.id}-react`}
                    members={members}
                />
            )}

            {/* Actions */}
            <div className="flex items-center gap-3 pt-1 text-xs">
                <button
                    onClick={onVote}
                    className={cn(
                        "flex items-center gap-1 rounded-full px-2 py-1 transition-colors",
                        post.voted_by_me ? "bg-indigo-100 text-indigo-700 font-semibold" : "hover:bg-slate-100 text-slate-500"
                    )}
                    data-testid={`post-vote-${post.id}`}
                >
                    <ThumbsUp className="w-3.5 h-3.5" /> {post.votes_count || 0}
                </button>
                <button
                    onClick={() => setShowComments(!showComments)}
                    className="flex items-center gap-1 rounded-full px-2 py-1 hover:bg-slate-100 text-slate-500"
                    data-testid={`post-toggle-comments-${post.id}`}
                >
                    <Reply className="w-3.5 h-3.5" /> {(post.comments || []).length}
                </button>
                {isProposition && !isConverted && onConvert && (
                    <Hint text="Convertir cette proposition en vrai projet avec pipeline, membres et deadline. Idéal quand une idée fait consensus (beaucoup de votes).">
                        <button
                            onClick={onConvert}
                            className="ml-auto flex items-center gap-1 rounded-full px-3 py-1 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-[11px]"
                            data-testid={`post-convert-${post.id}`}
                        >
                            <Rocket className="w-3.5 h-3.5" /> Créer projet
                        </button>
                    </Hint>
                )}
            </div>

            {/* Commentaires */}
            {showComments && (
                <div className="pt-2 border-t space-y-2">
                    {(post.comments || []).map((c) => {
                        const cMentionNames = mentionIdsToNames(c.mentions, members);
                        return (
                            <div key={c.id} className="flex gap-2 text-xs" data-testid={`comment-${c.id}`}>
                                <span className="w-6 h-6 rounded-full bg-slate-200 text-slate-700 text-[9px] flex items-center justify-center font-bold shrink-0">
                                    {(c.author?.first_name?.[0] || '?')}{(c.author?.last_name?.[0] || '')}
                                </span>
                                <div className="flex-1 min-w-0 rounded-md bg-slate-50 px-2.5 py-1.5">
                                    <div className="flex items-center justify-between">
                                        <span className="font-semibold text-slate-700">{c.author?.first_name} {c.author?.last_name}</span>
                                        <span className="text-[9px] text-slate-400">{timeAgo(c.created_at)}</span>
                                    </div>
                                    <div className="text-slate-700 mt-0.5 whitespace-pre-wrap break-words">
                                        <Linkify text={c.content} mentionNames={cMentionNames} />
                                    </div>
                                    {onReactComment && (
                                        <QuickReactionBar
                                            compact
                                            reactions={c.reactions}
                                            currentUserId={currentUser?.id}
                                            onToggle={(emoji) => onReactComment(c.id, emoji)}
                                            testIdPrefix={`comment-${c.id}-react`}
                                            members={members}
                                        />
                                    )}
                                </div>
                                {c.author_id === currentUser?.id && (
                                    <button onClick={() => onDeleteComment(c.id)} className="text-slate-300 hover:text-red-500 shrink-0">
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                    <div className="flex gap-2">
                        <MentionTextInput
                            value={commentInput}
                            onChange={setCommentInput}
                            onSubmit={submitComment}
                            members={members || []}
                            placeholder="Répondre… (@ pour mentionner)"
                            testId={`comment-input-${post.id}`}
                        />
                        <Button size="sm" onClick={submitComment} disabled={!commentInput.trim()} className="h-8 px-2" data-testid={`comment-submit-${post.id}`}>
                            <Send className="w-3.5 h-3.5" />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

function ConvertToProjectDialog({ post, open, onOpenChange, spaces, themes, members, onDone }) {
    const [form, setForm] = useState({
        space_id: spaces[0]?.id || '',
        theme_id: themes[0]?.id || '',
        name: '',
        importance: 6,
        deadline: '',
        members: [],
        goal: '',
    });
    const [submitting, setSubmitting] = useState(false);

    const toggleMember = (id) => {
        setForm((f) => ({
            ...f,
            members: f.members.includes(id) ? f.members.filter((m) => m !== id) : [...f.members, id],
        }));
    };

    const submit = async () => {
        if (!form.name.trim()) { toast.error('Nom du projet obligatoire'); return; }
        if (!form.deadline) { toast.error('Date butoir obligatoire'); return; }
        setSubmitting(true);
        try {
            const r = await hubAPI.convertToProject(post.id, {
                ...form,
                deadline: new Date(form.deadline).toISOString(),
            });
            onDone(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="convert-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Rocket className="w-5 h-5 text-emerald-500" /> Convertir en projet</DialogTitle>
                    <DialogDescription>
                        Cette proposition va devenir un projet officiel avec pipeline, membres et deadline. Le mur restera avec la mention « Converti ».
                    </DialogDescription>
                </DialogHeader>
                <div className="rounded-md bg-slate-50 p-2 text-xs text-slate-600 border">
                    <span className="font-semibold">Idée d&apos;origine :</span> {post.content.slice(0, 200)}{post.content.length > 200 ? '…' : ''}
                </div>
                <div className="space-y-3">
                    <div>
                        <Label>Nom du projet *</Label>
                        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex : Étude fiche CEE — X" data-testid="conv-name" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Espace *</Label>
                            <select value={form.space_id} onChange={(e) => setForm({ ...form, space_id: e.target.value })} className="w-full h-10 rounded-md border border-slate-200 px-2 text-sm" data-testid="conv-space">
                                {spaces.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label>Thème *</Label>
                            <select value={form.theme_id} onChange={(e) => setForm({ ...form, theme_id: e.target.value })} className="w-full h-10 rounded-md border border-slate-200 px-2 text-sm" data-testid="conv-theme">
                                {themes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Deadline *</Label>
                            <Input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} data-testid="conv-deadline" />
                        </div>
                        <div>
                            <Label>Importance : <span className="font-semibold text-indigo-600">{form.importance}/10</span></Label>
                            <input type="range" min="0" max="10" value={form.importance} onChange={(e) => setForm({ ...form, importance: parseInt(e.target.value) })} className="w-full mt-2" />
                        </div>
                    </div>
                    <div>
                        <Label>But final</Label>
                        <Textarea rows={2} value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} placeholder="Qu'est-ce qui définira le succès ?" />
                    </div>
                    <div>
                        <Label>Membres impliqués ({form.members.length})</Label>
                        <div className="max-h-40 overflow-y-auto rounded-md border p-2 space-y-1 mt-1">
                            {members.map((m) => (
                                <label key={m.id} className="flex items-center gap-2 p-1 rounded hover:bg-slate-50 cursor-pointer">
                                    <Checkbox checked={form.members.includes(m.id)} onCheckedChange={() => toggleMember(m.id)} />
                                    <span className="text-xs">{m.first_name} {m.last_name}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
                    <Button onClick={submit} disabled={submitting} className="bg-emerald-500 hover:bg-emerald-600" data-testid="conv-submit">
                        {submitting ? '…' : 'Créer le projet'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
